import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, RefreshCw, Boxes } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import { cbApi, resolveSyncError, useConnectedBusinessesEnabled } from '../../lib/connectedBusinesses';
import { GlassCard } from '../GlassCard';
import GlassTable from '../GlassTable';

/* Bakemill tenant only — Inventory screen: ERP inventory value, SKU stock, store-wise stock.
   Uses the shared (deduped) /bakemill-summary call; renders nothing for other tenants. */

const NA = 'Not available from ERP';
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const inr = (n) => (n == null ? '—' : '₹' + Math.round(n).toLocaleString('en-IN')); // same as Home KPIs
const qty = (n) => (n == null ? '—' : n.toLocaleString('en-IN'));
const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

function normSku(s, i) {
  const threshold = num(s.low_stock_threshold ?? s.threshold ?? s.reorder_level);
  const current = num(s.current_stock ?? s.qty);
  return {
    id: s.id ?? `${s.sku}-${i}`, sku: s.sku ?? s.sku_code ?? '—', name: s.name ?? '—', unit: s.unit ?? '—',
    current_stock: current, cost_price: num(s.cost_price), stock_value: num(s.stock_value),
    low: threshold != null && current != null && current < threshold,
  };
}

export default function BakemillInventory() {
  const enabled = useConnectedBusinessesEnabled();
  if (enabled !== true) return null;
  return <BakemillInventoryBody />;
}

function BakemillInventoryBody() {
  const { token } = useAuth();
  const toast = useToast();
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh) => {
    if (refresh) setRefreshing(true);
    try {
      const r = await cbApi.summary(token, refresh);
      setRes(r); setError('');
      if (refresh) toast.show('Bakemill ERP synced', 'success');
    } catch (e) {
      const info = await resolveSyncError(token, e);
      setError(info.detail);
      if (refresh) toast.show(`Bakemill ERP sync failed: ${info.detail}`, 'error');
    } finally { setRefreshing(false); }
  }, [token, toast]);

  useEffect(() => { load(false); }, [load]);

  const d = isObj(res?.data) ? res.data : {};
  const invValue = num(d.inventory_value);
  const skus = Array.isArray(d.sku_stock) ? d.sku_stock.filter(isObj).map(normSku) : [];
  const shops = Array.isArray(d.retail_showrooms) ? d.retail_showrooms : Array.isArray(d.showrooms) ? d.showrooms : null;

  return (
    <div className="space-y-4 mb-6" data-testid="bakemill-inventory">
      <GlassCard dark className="p-5" testId="bm-inv-value-card">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Inventory Value · Bakemill ERP</p>
            {!res && !error ? <div className="h-8 w-40 mt-1 rounded bg-white/10 animate-pulse" /> : (
              <p className={`mt-1 tabular-nums ${invValue == null ? 'text-white/40 text-base font-semibold' : 'text-white text-3xl font-extrabold'}`} data-testid="bm-inv-value">
                {invValue == null ? NA : inr(invValue)}
              </p>
            )}
            {error && <p className="text-red-400 text-xs mt-1" data-testid="bm-inv-error">Bakemill ERP sync failed: {error}</p>}
          </div>
          <span className="text-white/40 text-xs self-center" title={res?.last_synced_at ? fmtDateTime(res.last_synced_at) : undefined} data-testid="bm-inv-last-synced">
            {res?.last_synced_at ? `Last synced: ${fmtRelative(res.last_synced_at)}` : 'Not yet synced'}
          </span>
          <button type="button" onClick={() => load(true)} disabled={refreshing} data-testid="bm-inv-refresh-btn"
            className="self-center flex items-center gap-1.5 h-9 px-3 rounded-xl border border-white/15 bg-white/5 text-white text-sm font-semibold hover:bg-white/10 disabled:opacity-60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </GlassCard>

      <section data-testid="bm-sku-stock">
        <h2 className="text-white font-bold text-base mb-2">SKU Stock <span className="text-white/40 text-xs font-semibold">· Bakemill ERP{skus.length ? ` · ${skus.length} SKUs` : ''}</span></h2>
        {res && skus.length === 0 ? <p className="text-white/40 text-sm" data-testid="bm-sku-stock-na">{NA}</p> : (
          <div className={skus.length > 20 ? 'max-h-[560px] overflow-y-auto rounded-2xl' : ''}>
            <GlassTable testId="bm-sku-table" data={skus} loading={!res && !error} emptyMessage={NA}
              columns={[
                { key: 'sku', label: 'SKU' },
                { key: 'name', label: 'Name', render: (v, r) => <span className={r.low ? 'text-red-400 font-semibold' : 'text-white'}>{v}</span> },
                { key: 'unit', label: 'Unit' },
                { key: 'current_stock', label: 'Current Stock', align: 'right', render: (v, r) => <span className={`tabular-nums font-bold ${r.low ? 'text-red-400' : 'text-white'}`} data-low={r.low || undefined}>{qty(v)}</span> },
                { key: 'cost_price', label: 'Cost Price', align: 'right', render: v => <span className="tabular-nums">{inr(v)}</span> },
                { key: 'stock_value', label: 'Stock Value', align: 'right', render: v => <span className="tabular-nums font-semibold text-white">{inr(v)}</span> },
              ]} />
          </div>
        )}
      </section>

      <section data-testid="bm-store-stock">
        <h2 className="text-white font-bold text-base mb-2">Store-wise Stock <span className="text-white/40 text-xs font-semibold">· Bakemill ERP</span></h2>
        {res && !shops?.length ? <p className="text-white/40 text-sm" data-testid="bm-store-stock-na">{NA}</p> : (
          <div className="space-y-2">{(shops || []).filter(isObj).map((s, i) => <StorePanel key={s.id || s.name || i} shop={s} index={i} />)}</div>
        )}
      </section>
    </div>
  );
}

function StorePanel({ shop, index }) {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(shop.stock_items) ? shop.stock_items.filter(isObj) : [];
  return (
    <GlassCard dark className="p-0 overflow-hidden" testId={`bm-store-${index}`}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} data-testid={`bm-store-${index}-toggle`}
        className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400">
        <Boxes size={16} className="text-teal-400 shrink-0" />
        <span className="flex-1 min-w-0 truncate text-white font-semibold text-sm">{shop.name || '—'}{shop.city && <span className="text-white/40 font-normal"> · {shop.city}</span>}</span>
        <span className="text-white/40 text-xs">{items.length} items</span>
        <ChevronDown size={16} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4" data-testid={`bm-store-${index}-items`}>
          {items.length === 0 ? <p className="text-white/40 text-sm py-2">No stock items reported for this showroom</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-white/40 text-[10px] uppercase tracking-wider border-b border-white/10">
                <th className="py-2 text-left font-bold">SKU ID</th><th className="py-2 text-left font-bold">Name</th><th className="py-2 text-right font-bold">Current Stock</th>
              </tr></thead>
              <tbody>{items.map((it, j) => (
                <tr key={it.id ?? j} className="border-b border-white/5">
                  <td className="py-1.5 text-white/60 font-mono text-xs">{it.sku ?? it.sku_code ?? it.id ?? '—'}</td>
                  <td className="py-1.5 text-white">{it.name ?? '—'}</td>
                  <td className="py-1.5 text-right text-white font-bold tabular-nums">{qty(num(it.current_stock ?? it.qty))}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}
    </GlassCard>
  );
}
